import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { Select } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowLeft, IconLink, IconExternalLink, IconChevronDown, IconChevronRight, IconTrash, IconX } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { ProfPortfolio } from '@/models/profPortfolio.ts'
import { ThemeDefinitions } from '@/models/drive.ts'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import InlineEditInput from '@/components/portal/InlineEditInput.tsx'
import TagInput from '@/components/portal/TagInput.tsx'
import AutoSaveTextarea from '@/components/portal/AutoSaveTextarea.tsx'
import InspectorPreviewLayout from '@/components/portal/InspectorPreviewLayout.tsx'
import ProjectSelect from '@/components/portal/ProjectSelect.tsx'
import { ProfTag } from '@/models/profTag.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'

const PROJECT_ORDER_OPTIONS = [
  { value: '', label: 'Standard' },
  { value: 'title', label: 'Name (A-Z)' },
  { value: '-title', label: 'Name (Z-A)' },
  { value: 'event_date', label: 'Datum (älteste zuerst)' },
  { value: '-event_date', label: 'Datum (neueste zuerst)' },
  { value: 'created_at', label: 'Erstellt (älteste zuerst)' },
  { value: '-created_at', label: 'Erstellt (neueste zuerst)' },
]

export default function PortfolioDetailPage() {
  const { portfolioId } = useParams<{ portfolioId: string }>()
  const { getPortfolio } = useClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portfolio', portfolioId],
    queryFn: async () => {
      const res = await getPortfolio(portfolioId!)
      return res.data as ProfPortfolio
    },
    enabled: !!portfolioId,
  })

  if (isLoading) return <LoadBig />

  if (error || !data) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-error">Fehler beim Laden des Portfolios</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4">
      <Link to="/portal/portfolios" className="inline-flex items-center gap-1 text-sm opacity-60 hover:opacity-100 transition-opacity mb-4">
        <IconArrowLeft size={16} />
        Portfolios
      </Link>

      <PortfolioDetailContent
        portfolio={data}
        portfolioId={portfolioId!}
      />
    </div>
  )
}

interface PortfolioDetailContentProps {
  portfolio: ProfPortfolio
  portfolioId: string
}

function PortfolioDetailContent({ portfolio, portfolioId }: PortfolioDetailContentProps) {
  const { updatePortfolio, createTag } = useClient()
  const queryClient = useQueryClient()
  const portalContext = usePortalContext()
  const allTags = portalContext?.account?.tags ?? []

  // Track loading states for tag/project add/remove
  const [loadingTagAction, setLoadingTagAction] = useState<string | null>(null)
  const [loadingProjectAction, setLoadingProjectAction] = useState<string | null>(null)

  // Collapsible blacklist sections
  const [showBlacklistTags, setShowBlacklistTags] = useState(portfolio.blacklist_tags.length > 0)
  const [showBlacklistProjects, setShowBlacklistProjects] = useState(portfolio.blacklist_projects.length > 0)

  // Preview — the iframe itself lives in InspectorPreviewLayout, we only trigger reloads
  const [iframeKey, setIframeKey] = useState(0)

  const publicUrl = `${window.location.origin}/p/${portfolioId}`
  // `_preview=1` marks the iframe document as the portal's own preview, so it
  // reports no statistics (see statsTracking.ts).
  const previewUrl = `${publicUrl}?_preview=1`

  const refreshPreview = useCallback(() => {
    setIframeKey(k => k + 1)
  }, [])

  async function saveAndRefresh(field: string, value: any) {
    try {
      await updatePortfolio(portfolioId, { [field]: value })
      queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] })
      refreshPreview()
    } catch (e: any) {
      console.error('Error updating portfolio:', e)
      toast.error('Fehler beim Aktualisieren')
    }
  }

  async function saveFormDataAndRefresh(formData: FormData) {
    try {
      await updatePortfolio(portfolioId, formData)
      queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] })
      refreshPreview()
    } catch (e: any) {
      console.error('Error updating portfolio:', e)
      toast.error('Fehler beim Aktualisieren')
    }
  }

  async function handleTitleChange(newTitle: string) {
    await updatePortfolio(portfolioId, { title: newTitle })
    queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] })
    queryClient.invalidateQueries({ queryKey: ['portfolios'] })
    refreshPreview()
    toast('Portfolioname aktualisiert', { closeButton: true, autoClose: 2000 })
  }

  // Whitelist tags
  async function handleAddWhitelistTag(tag: ProfTag) {
    setLoadingTagAction(`add-wl-${tag.id}`)
    const newIds = [...portfolio.whitelist_tags.map(t => t.id), tag.id]
    await saveAndRefresh('whitelist_tag_ids', newIds)
    setLoadingTagAction(null)
  }

  async function handleRemoveWhitelistTag(tag: ProfTag) {
    setLoadingTagAction(`rm-wl-${tag.id}`)
    const newIds = portfolio.whitelist_tags.filter(t => t.id !== tag.id).map(t => t.id)
    await saveAndRefresh('whitelist_tag_ids', newIds)
    setLoadingTagAction(null)
  }

  // Blacklist tags
  async function handleAddBlacklistTag(tag: ProfTag) {
    setLoadingTagAction(`add-bl-${tag.id}`)
    const newIds = [...portfolio.blacklist_tags.map(t => t.id), tag.id]
    await saveAndRefresh('blacklist_tag_ids', newIds)
    setLoadingTagAction(null)
  }

  async function handleRemoveBlacklistTag(tag: ProfTag) {
    setLoadingTagAction(`rm-bl-${tag.id}`)
    const newIds = portfolio.blacklist_tags.filter(t => t.id !== tag.id).map(t => t.id)
    await saveAndRefresh('blacklist_tag_ids', newIds)
    setLoadingTagAction(null)
  }

  async function handleCreateTag(name: string): Promise<ProfTag> {
    const res = await createTag({ name })
    queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
    return res.data
  }

  // Projects whitelist/blacklist
  async function handleAddWhitelistProject(projectId: string) {
    setLoadingProjectAction(`add-wl-${projectId}`)
    const newIds = [...portfolio.whitelist_projects.map(p => p.id), projectId]
    await saveAndRefresh('whitelist_project_ids', newIds)
    setLoadingProjectAction(null)
  }

  async function handleRemoveWhitelistProject(projectId: string) {
    setLoadingProjectAction(`rm-wl-${projectId}`)
    const newIds = portfolio.whitelist_projects.filter(p => p.id !== projectId).map(p => p.id)
    await saveAndRefresh('whitelist_project_ids', newIds)
    setLoadingProjectAction(null)
  }

  async function handleAddBlacklistProject(projectId: string) {
    setLoadingProjectAction(`add-bl-${projectId}`)
    const newIds = [...portfolio.blacklist_projects.map(p => p.id), projectId]
    await saveAndRefresh('blacklist_project_ids', newIds)
    setLoadingProjectAction(null)
  }

  async function handleRemoveBlacklistProject(projectId: string) {
    setLoadingProjectAction(`rm-bl-${projectId}`)
    const newIds = portfolio.blacklist_projects.filter(p => p.id !== projectId).map(p => p.id)
    await saveAndRefresh('blacklist_project_ids', newIds)
    setLoadingProjectAction(null)
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(publicUrl)
    toast('Link kopiert', { autoClose: 2000 })
  }

  async function handleThumbnailUpload(file: File) {
    const formData = new FormData()
    formData.append('thumbnail', file)
    await saveFormDataAndRefresh(formData)
    toast('Thumbnail aktualisiert', { autoClose: 2000 })
  }

  async function handleThumbnailDelete() {
    await saveAndRefresh('thumbnail_clear', true)
    toast('Thumbnail entfernt', { autoClose: 2000 })
  }

  return (
    <div>
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-y-3 mb-6">
        <InlineEditInput value={portfolio.title} onSave={handleTitleChange} className="min-w-[16rem]">
          <h1 className="text-2xl font-bold">{portfolio.title}</h1>
        </InlineEditInput>
        {/* Mobile: wraps onto its own row below the title, buttons side by side.
            Desktop: right of the title. */}
        <div className="flex items-center gap-2 w-full md:w-auto md:flex-shrink-0">
          <Button className="flex-1 md:flex-none" size="sm" color="primary" variant="outline" onClick={handleCopyLink}>
            <IconLink size={16} />
            Link kopieren
          </Button>
          <a className="flex-1 md:flex-none" href={publicUrl} target="_blank" rel="noopener noreferrer">
            <Button className="w-full md:w-auto" size="sm" variant="outline" color="ghost">
              <IconExternalLink size={16} />
              Kunden-Ansicht
            </Button>
          </a>
        </div>
      </div>

      <InspectorPreviewLayout previewUrl={previewUrl} reloadKey={iframeKey}>
          {/* Description */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-3">Beschreibung</h3>
            <AutoSaveTextarea
              value={portfolio.description}
              placeholder="Öffentliche Beschreibung..."
              onSave={(val) => saveAndRefresh('description', val)}
            />
          </section>

          {/* Internal description */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-3">Interne Notizen</h3>
            <AutoSaveTextarea
              value={portfolio.internal_description}
              placeholder="Nur für dich sichtbar..."
              onSave={(val) => saveAndRefresh('internal_description', val)}
            />
          </section>

          {/* Whitelist tags */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-1">Tags einbinden</h3>
            <p className="text-sm opacity-60 mb-3">Nur Projekte mit diesen Tags werden angezeigt. Leer = alle Projekte.</p>
            <TagInputWithLoading
              selectedTags={portfolio.whitelist_tags}
              availableTags={allTags}
              onAddTag={handleAddWhitelistTag}
              onRemoveTag={handleRemoveWhitelistTag}
              onCreateTag={handleCreateTag}
              loadingTagAction={loadingTagAction}
              actionPrefix="wl"
            />
          </section>

          {/* Whitelist projects */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-1">Projekte einbinden</h3>
            <p className="text-sm opacity-60 mb-3">Nur diese Projekte werden angezeigt. Leer = alle (nach Tag-Filtern).</p>
            <ProjectListWithLoading
              projects={portfolio.whitelist_projects}
              onRemove={handleRemoveWhitelistProject}
              loadingAction={loadingProjectAction}
              actionPrefix="wl"
            />
            <ProjectSelect
              selectedProjectIds={portfolio.whitelist_projects.map(p => p.id)}
              onAdd={handleAddWhitelistProject}
              onRemove={handleRemoveWhitelistProject}
              hideBadges
            />
          </section>

          {/* Blacklist tags (collapsible) */}
          <section className="bg-secondary">
            <button
              className="w-full flex items-center gap-2 p-4 text-left"
              onClick={() => setShowBlacklistTags(!showBlacklistTags)}
            >
              {showBlacklistTags ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              <h3 className="font-semibold flex-1">Tags ausschließen</h3>
              {portfolio.blacklist_tags.length > 0 && (
                <span className="badge badge-sm">{portfolio.blacklist_tags.length}</span>
              )}
            </button>
            {showBlacklistTags && (
              <div className="px-4 pb-4">
                <p className="text-sm opacity-60 mb-3">Projekte mit diesen Tags werden ausgeschlossen — auch wenn sie oben eingebunden wurden.</p>
                <TagInputWithLoading
                  selectedTags={portfolio.blacklist_tags}
                  availableTags={allTags}
                  onAddTag={handleAddBlacklistTag}
                  onRemoveTag={handleRemoveBlacklistTag}
                  onCreateTag={handleCreateTag}
                  loadingTagAction={loadingTagAction}
                  actionPrefix="bl"
                />
              </div>
            )}
          </section>

          {/* Blacklist projects (collapsible) */}
          <section className="bg-secondary">
            <button
              className="w-full flex items-center gap-2 p-4 text-left"
              onClick={() => setShowBlacklistProjects(!showBlacklistProjects)}
            >
              {showBlacklistProjects ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              <h3 className="font-semibold flex-1">Projekte ausschließen</h3>
              {portfolio.blacklist_projects.length > 0 && (
                <span className="badge badge-sm">{portfolio.blacklist_projects.length}</span>
              )}
            </button>
            {showBlacklistProjects && (
              <div className="px-4 pb-4">
                <p className="text-sm opacity-60 mb-3">Diese Projekte werden ausgeschlossen — auch wenn sie oben eingebunden wurden.</p>
                <ProjectListWithLoading
                  projects={portfolio.blacklist_projects}
                  onRemove={handleRemoveBlacklistProject}
                  loadingAction={loadingProjectAction}
                  actionPrefix="bl"
                />
                <ProjectSelect
                  selectedProjectIds={portfolio.blacklist_projects.map(p => p.id)}
                  onAdd={handleAddBlacklistProject}
                  onRemove={handleRemoveBlacklistProject}
                  hideBadges
                />
              </div>
            )}
          </section>

          {/* Theme */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-3">Theme</h3>
            <Select
              className="w-full"
              value={portfolio.theme_name || ''}
              onChange={e => saveAndRefresh('theme_name', e.target.value)}
            >
              <option value="">Kein Theme</option>
              {ThemeDefinitions.map(theme => (
                <option key={theme.theme_name} value={theme.theme_name}>
                  {theme.theme_display_name}
                </option>
              ))}
            </Select>
          </section>

          {/* Default project order */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-3">Projekt-Sortierung</h3>
            <Select
              className="w-full"
              value={portfolio.default_project_order || ''}
              onChange={e => saveAndRefresh('default_project_order', e.target.value)}
            >
              {PROJECT_ORDER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </section>

          {/* Thumbnail */}
          <section className="bg-secondary p-4">
            <h3 className="font-semibold mb-3">Thumbnail</h3>
            {portfolio.thumbnail && (
              <div className="relative inline-block mb-3">
                <img
                  src={portfolio.thumbnail}
                  alt="Portfolio Thumbnail"
                  className="w-40 h-28 object-cover rounded-lg"
                />
                <button
                  className="absolute top-1 right-1 btn btn-circle btn-xs bg-[black] bg-opacity-60 border-none text-[white] hover:bg-error"
                  onClick={handleThumbnailDelete}
                  title="Thumbnail entfernen"
                >
                  <IconTrash size={12} />
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="file-input file-input-bordered file-input-sm w-full"
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) handleThumbnailUpload(file)
              }}
            />
          </section>
      </InspectorPreviewLayout>
    </div>
  )
}

// TagInput wrapper that shows loading spinners
function TagInputWithLoading({
  selectedTags,
  availableTags,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  loadingTagAction,
  actionPrefix,
}: {
  selectedTags: ProfTag[]
  availableTags: ProfTag[]
  onAddTag: (tag: ProfTag) => void
  onRemoveTag: (tag: ProfTag) => void
  onCreateTag: (name: string) => Promise<ProfTag>
  loadingTagAction: string | null
  actionPrefix: string
}) {
  const isAdding = loadingTagAction?.startsWith(`add-${actionPrefix}`)

  return (
    <div className="relative">
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedTags.map(tag => {
            const isRemoving = loadingTagAction === `rm-${actionPrefix}-${tag.id}`
            return (
              <span
                key={tag.id}
                className="badge badge-outline inline-flex items-center gap-1.5"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color ? `#${tag.color}` : '#7EB6E6' }}
                />
                <span className="truncate max-w-[120px]">{tag.name}</span>
                {isRemoving ? (
                  <span className="loading loading-spinner loading-xs -mr-1" />
                ) : (
                  <button
                    className="hover:text-error -mr-1"
                    onClick={() => onRemoveTag(tag)}
                    disabled={loadingTagAction !== null}
                  >
                    <IconX size={14} />
                  </button>
                )}
              </span>
            )
          })}
        </div>
      )}
      <div className="relative">
        <TagInput
          selectedTags={[]}
          availableTags={availableTags.filter(t => !selectedTags.some(s => s.id === t.id))}
          onAddTag={onAddTag}
          onRemoveTag={() => {}}
          onCreateTag={onCreateTag}
        />
        {isAdding && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <span className="loading loading-spinner loading-xs" />
          </div>
        )}
      </div>
    </div>
  )
}

// Project list with names and loading remove buttons
function ProjectListWithLoading({
  projects,
  onRemove,
  loadingAction,
  actionPrefix,
}: {
  projects: { id: string; title: string }[]
  onRemove: (projectId: string) => void
  loadingAction: string | null
  actionPrefix: string
}) {
  if (projects.length === 0) return null

  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {projects.map(project => {
        const isRemoving = loadingAction === `rm-${actionPrefix}-${project.id}`
        return (
          <span
            key={project.id}
            className="badge badge-outline inline-flex items-center gap-1.5"
          >
            <span className="truncate max-w-[150px]">{project.title}</span>
            {isRemoving ? (
              <span className="loading loading-spinner loading-xs -mr-1" />
            ) : (
              <button
                className="hover:text-error -mr-1"
                onClick={() => onRemove(project.id)}
                disabled={loadingAction !== null}
              >
                <IconX size={14} />
              </button>
            )}
          </span>
        )
      })}
    </div>
  )
}
